import React from "react";
import moment from "moment-timezone";
import { v1 as uuidv1 } from "uuid";

import { fetchApi, withAccessToken } from "../../Network/NetworkService";
import { endpoint } from "../../Constants";
import { notif } from "../Ui/Notification";

import "../../Assets/Pages/dermatoSdr.css";
import IconTooltip from "../Ui/IconTooltip";

// Keep in sync with MAX_RANGE_DAYS in ReferralCampaignController.js.
const MAX_RANGE_DAYS = 31;

// Same rule as isValidTestEmail in ReferralCampaignController.js.
const isValidTestEmail = (s) => {
  const v = (s || "").trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) && !/[^\x20-\x7E]/.test(v);
};

// Campagne « parrainage » : email aux patients SATISFAITS (avis q1 positif,
// aucun avis négatif) d'une consultation aboutie,
// les invitant à partager leur expérience Feeli (Instagram / TikTok /
// WhatsApp). Instagram/TikTok ouvrent l'application ; WhatsApp pré-remplit le
// lien d'invitation personnel du patient (register?ref=<code>, code généré à
// l'envoi s'il n'existe pas).
class ReferralCampaign extends React.Component {
  constructor() {
    super();
    const today = moment().tz("Europe/Paris").format("YYYY-MM-DD");
    this.state = {
      startDate: today,
      endDate: today,
      testMode: false,
      testRealUser: false,
      testEmail: "",
      users: [],
      loading: false,
      sending: false,
      sendProgress: "0",
      usersLoaded: false,
      sendEvent: null,
      page: 1,
      totalPage: 0,
      countUsers: 0,
    };
  }

  componentDidMount() {
    this.fetchUsers();
  }

  componentWillUnmount() {
    if (this.state.sendEvent) {
      this.state.sendEvent.close();
    }
  }

  rangeDays = () =>
    moment(this.state.endDate).diff(moment(this.state.startDate), "days") + 1;

  rangeTooWide = () => this.rangeDays() > MAX_RANGE_DAYS;

  fetchUsers = () => {
    if (this.rangeTooWide()) {
      this.setState({ users: [], usersLoaded: true, loading: false });
      return;
    }
    this.setState({ loading: true });
    const url =
      "/referral-campaign/list?start=" +
      this.state.startDate +
      "&end=" +
      this.state.endDate +
      "&page=" +
      this.state.page;
    fetchApi(url)
      .then((res) => {
        this.setState({
          users: res.users || [],
          totalPage: res.countPages || 0,
          countUsers: res.countUsers || 0,
          usersLoaded: true,
          loading: false,
        });
      })
      .catch(() => {
        notif.addNotification(
          "Erreur",
          "Erreur de récupération des patients",
          "warning",
        );
        this.setState({ loading: false });
      });
  };

  handleStartChange = (e) => {
    const startDate = e.target.value;
    const endDate =
      moment(this.state.endDate).isBefore(moment(startDate)) ||
      !this.state.endDate
        ? startDate
        : this.state.endDate;
    this.setState(
      { startDate, endDate, users: [], usersLoaded: false, page: 1 },
      this.fetchUsers,
    );
  };

  handleEndChange = (e) => {
    this.setState(
      { endDate: e.target.value, users: [], usersLoaded: false, page: 1 },
      this.fetchUsers,
    );
  };

  updatePage(page) {
    this.setState({ users: [], page, loading: true }, () => {
      this.fetchUsers();
    });
  }

  sendToAll = () => {
    if (
      this.state.sending ||
      this.rangeTooWide() ||
      (!this.state.testMode && !this.state.users.length)
    )
      return;

    const { testMode, testRealUser, testEmail, countUsers } = this.state;
    const trimmedTest = (testEmail || "").trim();

    if (testMode && !isValidTestEmail(trimmedTest)) {
      notif.addNotification(
        "Erreur",
        "Renseignez une adresse email de test valide (caractères ASCII).",
        "warning",
      );
      return;
    }

    const confirmMsg = testMode
      ? testRealUser
        ? "Mode TEST RÉEL : envoyer 1 email à " +
          trimmedTest +
          " avec de vrais liens personnalisés pour CE compte ? Aucun autre utilisateur contacté."
        : "Mode TEST : envoyer 1 email d'exemple à " +
          trimmedTest +
          " ? Aucun utilisateur réel contacté."
      : "Envoyer l'invitation parrainage à " +
        countUsers +
        " patient(s) ? De vrais emails seront envoyés.";
    if (!window.confirm(confirmMsg)) return;

    const tokenId = uuidv1();
    const sendEvent = new EventSource(
      withAccessToken(endpoint + "/referral-campaign/sendProgress?token=" + tokenId),
    );
    this.setState({ sending: true, sendProgress: "0", sendEvent });

    let posted = false;
    let settled = false;
    let fallbackTimer = null;
    let watchdog = null;

    const reset = () => {
      if (settled) return;
      settled = true;
      clearTimeout(fallbackTimer);
      clearTimeout(watchdog);
      this.setState({ sending: false, sendProgress: "0", sendEvent: null });
      sendEvent.close();
    };

    const refreshAfterSend = () => {
      this.setState({ page: 1 }, this.fetchUsers);
    };

    // Watchdog anti-spinner : si le flux SSE se tait, l'envoi continue côté
    // serveur et l'historique fait foi — voir GdprWarning.jsx.
    const bumpWatchdog = () => {
      clearTimeout(watchdog);
      watchdog = setTimeout(() => {
        if (settled) return;
        notif.addNotification(
          "Info",
          "Envoi lancé. Consultez l'historique pour le résultat.",
          "info",
        );
        reset();
        refreshAfterSend();
      }, 30000);
    };

    const triggerSend = () => {
      if (posted) return;
      posted = true;
      clearTimeout(fallbackTimer);
      bumpWatchdog();
      fetchApi("/referral-campaign/send", {
        method: "POST",
        body: JSON.stringify({
          start: this.state.startDate,
          end: this.state.endDate,
          id: tokenId,
          testMode: testMode,
          testRealUser: testMode ? testRealUser : false,
          testEmail: testMode ? trimmedTest : null,
        }),
      }).catch(() => {
        notif.addNotification("Erreur", "Erreur d'envoi", "warning");
        reset();
      });
    };

    fallbackTimer = setTimeout(triggerSend, 5000);
    sendEvent.onopen = triggerSend;

    sendEvent.onmessage = (ev) => {
      if (posted) bumpWatchdog();
      if (ev.data === "start") {
        triggerSend();
        return;
      }
      if (ev.data.startsWith("fail&")) {
        const parts = ev.data.split("&");
        const total = parseInt(parts[1]) || 0;
        const failCount = parseInt(parts[2]) || 0;
        notif.addNotification(
          "Error",
          total + " envoyés, " + failCount + " échecs",
          "warning",
        );
        reset();
        refreshAfterSend();
      } else if (ev.data.includes("error")) {
        notif.addNotification(
          "Erreur",
          ev.data.split("&")[1] || "Erreur d'envoi",
          "warning",
        );
        reset();
      } else if (ev.data.startsWith("done&")) {
        const parts = ev.data.split("&");
        const total = parseInt(parts[1]) || 0;
        notif.addNotification(
          "Succès",
          testMode
            ? "Email de test envoyé à " + trimmedTest
            : total + " invitations parrainage envoyées",
          "success",
        );
        reset();
        refreshAfterSend();
      } else if (ev.data === "empty") {
        notif.addNotification("Info", "Aucun patient à traiter", "info");
        reset();
      } else {
        this.setState({ sendProgress: ev.data });
      }
    };

    sendEvent.onerror = () => {
      // Pré-POST : le fallback déclenche l'envoi. Post-POST : le watchdog
      // libère le spinner, l'envoi continue côté serveur.
    };
  };

  render() {
    const {
      startDate,
      endDate,
      users,
      loading,
      sending,
      sendProgress,
      usersLoaded,
      page,
      totalPage,
      countUsers,
      testMode,
      testRealUser,
      testEmail,
    } = this.state;

    const maxDate = moment().tz("Europe/Paris").format("YYYY-MM-DD");
    const tooWide = this.rangeTooWide();

    return (
      <div className="dermato-sdr-container">
        <div className="dermato-sdr-header">
          <h2 className="dermato-sdr-title">
            {"Campagne parrainage — Partage d'expérience"}
          </h2>
          <div>
            <button
              className="dermato-sdr-btn dermatosdr-btn-link"
              onClick={() =>
                this.props.history.push("/campagne-parrainage/clics")
              }
            >
              {"Suivi des clics"}
            </button>
            <button
              className="dermato-sdr-btn dermatosdr-btn-link"
              onClick={() =>
                this.props.history.push("/campagne-parrainage/historique")
              }
            >
              {"Historique des envois"}
            </button>
          </div>
        </div>

        <div className="dermato-sdr-card">
          <h3 className="dermato-sdr-card-title">Filtres</h3>
          <p style={{ color: "#888", fontSize: 14, marginTop: -8 }}>
            {
              "Patients SATISFAITS de leur consultation : consultation aboutie (terminée ou révisée, toutes spécialités) dans la période choisie, avec au moins un avis positif déposé (ressenti global « Satisfaisante » ou « Très satisfaisante », anciennes formulations du questionnaire comprises) et aucun avis négatif — un patient sans avis n'apparaît pas. Jamais invités par cette campagne. Les nouvelles consultations partent automatiquement (avis positif ou amélioration en suivi) ; cette campagne sert au stock antérieur. Dans l'email « Partagez votre expérience », les boutons Instagram et TikTok ouvrent l'application, et le bouton WhatsApp pré-remplit le lien d'invitation personnel du patient (register?ref=code — généré à l'envoi si absent). Chaque clic sur un bouton est journalisé — voir « Suivi des clics »."
            }
          </p>
          <div className="dermato-sdr-inline">
            <div className="dermato-sdr-form-group">
              <label>Du</label>
              <input
                type="date"
                className="dermato-sdr-input"
                value={startDate}
                max={maxDate}
                onChange={this.handleStartChange}
              />
            </div>
            <div className="dermato-sdr-form-group">
              <label>Au</label>
              <input
                type="date"
                className="dermato-sdr-input"
                value={endDate}
                min={startDate}
                max={maxDate}
                onChange={this.handleEndChange}
              />
            </div>
            <div style={{ alignSelf: "flex-end" }}>
              <button
                className="dermato-sdr-btn dermatosdr-btn-primary"
                onClick={this.sendToAll}
                disabled={sending || tooWide || (!testMode && !users.length)}
              >
                {sending
                  ? "Envoi... " + sendProgress + "%"
                  : testMode
                    ? "Envoyer un test"
                    : "Envoyer l'invitation parrainage"}
              </button>
            </div>
          </div>

          <div className="dermato-sdr-testmode">
            <label className="dermato-sdr-checkbox">
              <input
                type="checkbox"
                checked={testMode}
                disabled={sending}
                onChange={(e) => this.setState({ testMode: e.target.checked })}
              />
              <span>{"Mode test"}</span>
            </label>
            {testMode && (
              <React.Fragment>
                <input
                  type="email"
                  className="dermato-sdr-input"
                  style={{ maxWidth: 260 }}
                  placeholder="email de test"
                  value={testEmail}
                  disabled={sending}
                  onChange={(e) => this.setState({ testEmail: e.target.value })}
                />
                <label className="dermato-sdr-checkbox">
                  <input
                    type="checkbox"
                    checked={testRealUser}
                    disabled={sending}
                    onChange={(e) =>
                      this.setState({ testRealUser: e.target.checked })
                    }
                  />
                  <span>{"Test réel (mon compte)"}</span>
                </label>
                <span style={{ color: "#888", fontSize: 13 }}>
                  {testRealUser
                    ? "1 email envoyé à cette adresse avec le VRAI lien d'invitation WhatsApp du compte Feeli correspondant (son code parrainage est généré si absent). Aucun flag posé."
                    : "1 email d'exemple (données fictives, code d'exemple) envoyé à cette adresse — aucun utilisateur réel contacté."}
                </span>
              </React.Fragment>
            )}
          </div>
          {tooWide && (
            <p style={{ color: "#c62828", fontSize: 14, marginTop: 8 }}>
              {"Période trop large : maximum " + MAX_RANGE_DAYS + " jours."}
            </p>
          )}
        </div>

        <div className="dermato-sdr-card" style={{ marginTop: 24 }}>
          <h3 className="dermato-sdr-card-title">Patients ({countUsers})</h3>

          {loading ? (
            <div className="dermato-sdr-empty">
              <span className="loader" />
            </div>
          ) : usersLoaded && users.length === 0 ? (
            <div className="dermato-sdr-empty">
              {"Aucun patient éligible trouvé pour cette période."}
            </div>
          ) : users.length > 0 ? (
            <div>
              <table className="dermato-sdr-table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Email</th>
                    <th>{"Code parrainage"}</th>
                    <th>{"Avis"}</th>
                    <th>{"Dernière consultation"}</th>
                    <th>{"Consultations"}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u._id}>
                      <td>
                        {u.FName} {u.LName}
                      </td>
                      <td>{u.email}</td>
                      <td>
                        {u.referralCode ? (
                          <span style={{ fontWeight: 600 }}>
                            {u.referralCode}
                          </span>
                        ) : (
                          <span
                            style={{ color: "#888" }}
                            title="Généré automatiquement à l'envoi"
                          >
                            {"— (à l'envoi)"}
                          </span>
                        )}
                      </td>
                      <td>
                        <span style={{ color: "#2e7d32", fontWeight: 600 }}>
                          {u.avis || "Satisfait"}
                        </span>
                      </td>
                      <td>
                        {moment(u.latestRequestDate).format("DD/MM/YYYY HH:mm")}
                      </td>
                      <td>{u.requestCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="dermatosdr-pagination">
                <p style={{ margin: 0, color: "#888", fontSize: 14 }}>
                  Page <strong>{page}</strong> / {totalPage}
                </p>
                <div>
                  {page > 1 && (
                    <IconTooltip
                      icon="fa fa-chevron-left"
                      text="Page précédente"
                      onClick={() => this.updatePage(page - 1)}
                    />
                  )}
                  {page < totalPage && (
                    <IconTooltip
                      icon="fa fa-chevron-right"
                      text="Page suivante"
                      onClick={() => this.updatePage(page + 1)}
                    />
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  }
}

export default ReferralCampaign;
